import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface ModuleSection {
  title: string;
  content: string[];
  additionalSection?: {
    title: string;
    content: string[];
  };
}

const moduleContent: ModuleSection[] = [
  {
    title: 'Faith and Coping: How Spirituality Supports Resilience',
    content: [
      '<strong>Objective:</strong> Explore the role of positive and negative religious coping in managing stress and adversity.',
      '<br/>',
      '<strong>Key Topics:</strong>',
      '• Positive Religious Coping – seeking comfort, forgiveness, guidance, and strength from faith',
      '• Negative Religious Coping – feeling abandoned, punished, or distant from God',
      '• How to reframe spiritual struggles into growth opportunities',
      '• Building a supportive faith practice',
      '<br/>',
      '<strong>Discussion:</strong> When facing challenges, people turn to different forms of coping. Some find strength in faith ("God is with me in this"), while others struggle ("Why would God let this happen?"). The Brief RCOPE helps us understand both sides – how faith can heal, and how doubt can wound.'
    ]
  },
  {
    title: 'Positive Religious Coping',
    content: [
      'Positive religious coping involves seeking comfort, forgiveness, guidance, and strength from faith. It includes practices like prayer, meditation, and community support.',
      '<br/>',
      'Benefits: Reduces stress, increases resilience, fosters hope and meaning.'
    ]
  },
  {
    title: 'Negative Religious Coping',
    content: [
      'Negative religious coping may include feeling abandoned, punished, or distant from God. It can lead to increased distress and isolation.',
      '<br/>',
      'Reframing: Turn struggles into opportunities for growth by seeking support and reinterpreting experiences.'
    ]
  },
  {
    title: 'Reframing Spiritual Struggles',
    content: [
      'Spiritual struggles can be reframed into growth opportunities. This involves questioning beliefs, seeking guidance, and finding new meaning.',
      '<br/>',
      'Activity: Reflect on a spiritual struggle and how it led to personal growth.'
    ]
  },
  {
    title: 'Building a Supportive Faith Practice',
    content: [
      'Building a supportive faith practice includes regular prayer, gratitude journaling, and community involvement.',
      '<br/>',
      'Practical Activity: Create your Faith Reconnection Plan: identify one daily or weekly practice that renews your sense of divine presence (e.g., prayer, gratitude journaling, quiet reflection).'
    ],
    additionalSection: {
      title: 'Reflection Exercise',
      content: [
        'Recall a difficult experience. How did your spiritual beliefs help you cope – or make it harder? What helped you find peace again?'
      ]
    }
  },
  {
    title: 'Key Takeaway',
    content: [
      'Faith-based coping isn\'t about perfection in belief – it\'s about learning to find strength through surrender and trust.'
    ]
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      'You have completed Module 2: Faith and Coping: How Spirituality Supports Resilience! Now, let\'s reinforce your learning with a quick quiz.'
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your knowledge about faith and coping.',
        'The quiz will help you review key concepts about positive and negative religious coping.'
      ]
    }
  }
];

const ReligioTraditioCulturalModule2Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress('rtcM2');
  const totalSteps = moduleContent.length;

  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, false);
    } else {
      await saveProgress(currentStep, true);
      navigate('/panel/learn/');
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  const showCompletionMessage = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="module-form">
      <div className="module-container">
        <div
          className="module-left religio-traditional-cultural-panel"
        >
          <div className="module-header">
            <h1>Module 2</h1>
            <p className="module-subtitle">Faith and Coping: How Spirituality Supports Resilience</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Explore the role of positive and negative religious coping in managing stress and adversity.
            </p>
          </div>
        </div>

        <div className="module-right">
          <div className="module-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {safeCurrentStep + 1} of {totalSteps}
              </span>
            </div>

            <div className="module-section">
              <h2 className="module-title">
                {currentModule.title}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false}
            >
              {currentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>

            {showCompletionMessage ? (
              <button
                className="btn-primary"
                onClick={() => navigate('/panel/learn/religio-traditio-cultural-module-2/quiz')}
              >
                Take the Quiz
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReligioTraditioCulturalModule2Page;


